import merge from 'lodash-es/merge';
import Base from '../components/base';
import { decimalSeparator, thousandSeparator, timeZone } from '../utils/intl';
import DEFAULTS from './config/defaults';
import getChartConfig from './config/base-config';
import tooltipFormat from './config/tooltip';
import Highcharts from 'highcharts/highstock';

class ChartBase extends Base {
	constructor(el) {
		super(el);

		const liveTickUpdates = Object.hasOwn(this.options, 'liveTickUpdates')
			? this.options.liveTickUpdates
			: DEFAULTS.liveTickUpdates;

		this.chartOptions = {
			decimalPrecisionLeftYaxis: this.options.leftAxisDecimals || DEFAULTS.decimalPrecision,
			decimalPrecisionRightYaxis: this.options.rightAxisDecimals || DEFAULTS.decimalPrecision,
			showTimeInTooltip: Object.hasOwn(this.options, 'showTimeInTooltip')
				? this.options.showTimeInTooltip
				: DEFAULTS.showTimeInTooltip,
			leftAxisTitle: this.options.leftAxisTitle,
			rightAxisTitle: this.options.rightAxisTitle,
			rightSecondaryAxisTitle: this.options.rightSecondaryAxisTitle,
			useOrdinal: Object.hasOwn(this.options, 'useOrdinal') ? this.options.useOrdinal : DEFAULTS.useOrdinal,
		};

		this.chartContainer = this.refs.chartContainer || el;
		this.config = getChartConfig(this.chartOptions);

		// Bind this scope
		this.onTick = this.onTick.bind(this);

		// Event listeners
		this.toggleTickListener(liveTickUpdates);
	}

	start() {
		// Set global Highcharts options
		Highcharts.setOptions({
			time: {
				timezone: timeZone,
			},

			lang: {
				decimalPoint: decimalSeparator,
				thousandsSep: thousandSeparator,
			},
		});

		// Create the chart
		this.chart = Highcharts.stockChart(this.chartContainer, this.config);
		this.afterRenderChart(this.chart);

		window.chart = this.chart;
	}

	// eslint-disable-next-line no-unused-vars
	afterRenderChart(chart) {
		// Noop
	}

	// eslint-disable-next-line no-unused-vars
	onGetChartDataSuccess(data) {
		// Noop
	}

	// eslint-disable-next-line no-unused-vars
	hasQuotes(apiResponse) {
		// Noop
	}

	onFetchDataError(error) {
		this.removeAllSeries();

		this.feedbackMessage = this.labelDataError;

		console.warn(error);
	}

	onTick(tick) {
		const chartSeries = this.chart.series;

		if (ChartBase.tickShouldBeAddedToChart(chartSeries, tick)) {
			chartSeries.forEach((series) => {
				ChartBase.addPointToChart(tick, series);
			});

			this.feedbackMessage = '';

			this.chart.redraw();
		}
	}

	async getChartData(endpoint) {
		this.isLoading = true;

		try {
			const data = await ChartBase.fetchData(endpoint);

			this.onGetChartDataSuccess(data);

			this.feedbackMessage = this.hasQuotes(data) ? '' : this.labelNoData;
		} catch (error) {
			this.onFetchDataError(error);
		} finally {
			this.isLoading = false;
		}
	}

	extendConfig(configExtension) {
		merge(this.config, configExtension);
	}

	updateChartConfig(useDateTime, useOrdinal, redraw) {
		this.chart.update(
			{
				tooltip: {
					formatter() {
						return tooltipFormat(this, useDateTime);
					},
				},
				xAxis: {
					ordinal: useOrdinal,
				},
			},
			redraw,
		);
	}

	removeAllSeries(redraw = true) {
		const { chart } = this;
		const { series } = chart;

		if (!series.length) {
			return;
		}

		while (series.length) {
			chart.series[0].remove(false);
		}

		if (redraw) {
			chart.redraw();
		}
	}

	toggleTickListener(on) {
		if (on) {
			this.eventbus.on(`tick:${this.options.isin}`, this.onTick);
		} else {
			this.eventbus.off(`tick:${this.options.isin}`, this.onTick);
		}
	}

	static fetchData(endpoint) {
		return fetch(endpoint, {
			credentials: 'same-origin',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
				'content-type': 'application/json',
			},
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(`${response.statusText} (${response.status})`);
				}

				return response.json();
			})
			.catch((error) => {
				console.warn('Network or parsing error:', error);
			});
	}

	static getTickData(tick, tickProperty) {
		const timestamp = tick.DT;
		const tickValue = tick[tickProperty];

		return [timestamp, tickValue];
	}

	static tickShouldBeAddedToChart(chartSeries, tick) {
		if (!chartSeries || chartSeries.length < 1) {
			return false;
		}

		if (tick.Bid === 0 && tick.Ask === 0 && tick.ReferencePrice === 0) {
			return false;
		}

		return true;
	}

	static addPointToChart(data, series, redraw = false) {
		// We need to make sure the id starts with a capital letter, because the properties returned by the API use uppercase
		const formattedSeriesId = series.options.id.charAt(0).toUpperCase() + series.options.id.slice(1);
		const point = ChartBase.getTickData(data, formattedSeriesId);

		// If the value part of the point equals zero, we do not want to plot it in the chart
		if (point[1] === 0) {
			return;
		}

		series.addPoint(point, redraw);
	}

	get isLoading() {
		return this.el.classList.contains('is-loading');
	}

	set isLoading(loading) {
		this.el.classList.toggle('is-loading', loading);
	}

	get labelNoData() {
		return this.options.labelNoData || DEFAULTS.labelNoData;
	}

	get labelDataError() {
		return this.options.labelDataError || DEFAULTS.labelDataError;
	}

	get feedbackMessage() {
		return this.el.dataset.message || '';
	}

	set feedbackMessage(msg = null) {
		if (msg) {
			this.el.setAttribute('data-message', msg);
		} else {
			this.el.removeAttribute('data-message');
		}
	}
}

export default ChartBase;
